'use client';
import { WEB_GREY_REG } from '@mnd-frontend/ui/shared/tokens';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import Collapsible from '../Collapsible';
import type { CollapsibleItemBaseProps } from '../Collapsible/index.type';

const AccordionWrapper = styled.ul`
    display: flex;
    flex-direction: column;
    list-style: none;
    padding: 0;
    margin: 0;
`;

const StyledItem = styled.li<{ $hasBorder: boolean }>`
  ${({ $hasBorder }) =>
    $hasBorder &&
    css`
    border-bottom: 1px solid ${WEB_GREY_REG};

    &:first-of-type {
      border-top: 1px solid ${WEB_GREY_REG};
    }
  `};
`;

export type AccordionItems = Array<CollapsibleItemBaseProps>;

type AccordionProps = {
  items: AccordionItems;
  /**
   * 'True' allows event propagation.
   * This will immediately close collapsible content wherever collapsible content is clicked.
   * Use only when e.g. tracker is required in the content.
   */
  hasContentClickHandler?: boolean;
  hasBorder?: boolean;
  forceClose?: boolean;
};

const Accordion = ({
  items,
  hasContentClickHandler = false,
  forceClose = false,
  hasBorder = false,
}: AccordionProps) => {
  const [itemsMap, setItemsMap] = useState<boolean[]>(() => items.map(_item => false));

  const clickHandler = (index: number) => {
    setItemsMap(itemsMap.map((_, i) => i === index && !itemsMap[index]));
  };

  return (
    <AccordionWrapper>
      {items.map((item, index) => (
        <StyledItem key={item.id} onClick={() => clickHandler(index)} $hasBorder={hasBorder}>
          <Collapsible
            key={`accordion-item${index}`}
            isExpandedViaParent={forceClose || itemsMap[index]}
            hasContentClickHandler={hasContentClickHandler}
            {...item}
          />
        </StyledItem>
      ))}
    </AccordionWrapper>
  );
};

export default Accordion;
